// src/components/pages/Admin/SubmissionsPage.js

import React, { useState, useEffect } from "react";
import {
  Typography,
  Container,
  CircularProgress,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
} from "@mui/material";
import { useTheme } from "styled-components";
import { fetchAllSubmissions } from "../../../services/submissionService";
import CustomButton from "../../pages/EventManager/atoms/CustomButton";
import { useNavigate } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

const SubmissionsPage = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const loadSubmissions = async () => {
      try {
        const data = await fetchAllSubmissions();
        console.log("submission data: ", data);
        setSubmissions(data);
      } catch (err) {
        console.error("Failed to fetch submissions:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadSubmissions();
  }, []);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, fontFamily: "basic-sans" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 2,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          All Submissions
        </Typography>
        <CustomButton
          onClick={() => navigate("/challenge/admin/events")}
          theme={theme}
          sx={{
            backgroundColor: theme.colors.mediumPurple,
            "&:hover": {
              backgroundColor: theme.colors.red,
            },
          }}
        >
          Back to Events
        </CustomButton>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 4,
          }}
        >
          <CircularProgress color="primary" />
        </Box>
      ) : error ? (
        <Alert severity="error">
          Failed to load submissions. Please try again later.
        </Alert>
      ) : submissions.length === 0 ? (
        <Typography variant="body1">No submissions available.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table aria-label="submissions table">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Title</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Creator UID</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submissions.map((submission) => (
                <Row
                  key={submission.id}
                  submission={submission}
                  theme={theme}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default SubmissionsPage;

/**
 * Row Component to handle expandable content
 */
const Row = ({ submission, theme }) => {
  const [open, setOpen] = useState(false);

  const {
    title,
    description,
    price,
    creatorUid,
    startDate,
    endDate,
    // Add other fields as necessary
  } = submission;

  // Format timestamps to readable dates
  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset" },
          backgroundColor: theme.colors.mediumPurple,
          color: "white",
        }}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            sx={{ color: "white" }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {title || "Untitled"}
        </TableCell>
        <TableCell>{description || "No Description"}</TableCell>
        <TableCell>${price !== undefined ? price : "N/A"}</TableCell>
        <TableCell>{creatorUid || "N/A"}</TableCell>
        <TableCell>{formatDate(startDate)}</TableCell>
        <TableCell>{formatDate(endDate)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                Payment Data
              </Typography>
              {submission.paymentData && submission.paymentData.length > 0 ? (
                <Table size="small" aria-label="payment">
                  <TableHead>
                    <TableRow>
                      <TableCell>Payment #</TableCell>
                      <TableCell>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {submission.paymentData.map((payment, index) => (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>${payment.Status}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2">
                  No payment data available.
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
